import { actionSx, FONT_UI } from "@/src/components/common/broadsheet/tokens";
import { Box } from "@mui/material";
import { fileSizeFormatter } from "@/src/utils/fileSizeFormatter";
import type ArticleBody from "@/src/_types/Article/article-body";
import { getArticlePdfUrl } from "@/src/utils/paths";

type ArticleDownloadBoxProps = {
  data: Partial<ArticleFiles & ArticleBody>;
};

const FileButton = ({
  href,
  label,
  size,
  variant = "outline",
}: {
  href: string;
  label: string;
  size?: number | null;
  variant?: "solid" | "outline" | "kicker";
}) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="bf-ui"
    sx={{
      ...actionSx(variant, "small"),
      width: "100%",
      mb: 0.5,
      justifyContent: "space-between",
      gap: 0.5,
    }}
  >
    <Box component="span">{label}</Box>
    {size ? (
      <Box
        component="span"
        sx={{
          flex: "none",
          fontFamily: FONT_UI,
          fontSize: "0.6875rem",
          fontWeight: 400,
          whiteSpace: "nowrap",
        }}
      >
        {fileSizeFormatter(size)}
      </Box>
    ) : null}
  </Box>
);

const ArticleDownloadBox = ({
  data,
  journalAbbr,
  articleId,
}: {
  data: ArticleDownloadBoxProps["data"];
  journalAbbr: string;
  articleId: number;
}) => (
  <Box sx={{ mb: 0.5 }}>
    <FileButton
      href={getArticlePdfUrl(journalAbbr, articleId)}
      label="Download PDF"
      size={data?.pdf_file_size as number}
      variant="kicker"
    />

    {data?.feature_audio?.map((file: any, index: number) =>
      file.size ? (
        <FileButton
          key={`audio-${index}`}
          href={file.url}
          label="Audio file"
          size={file.size}
        />
      ) : null,
    )}

    {data?.feature_video?.map((file: any, index: number) =>
      file.size ? (
        <FileButton
          key={`video-${index}`}
          href={file.url}
          label="Video file"
          size={file.size}
        />
      ) : null,
    )}

    {data?.feature_slide?.map((file: any, index: number) =>
      file.size ? (
        <FileButton
          key={`slide-${index}`}
          href={file.url}
          label="Slides"
          size={file.size}
        />
      ) : null,
    )}

    {data?.supplementary_material?.[0] ? (
      <FileButton
        href={data.supplementary_material[0].url ?? "#"}
        label="Supplementary material"
      />
    ) : null}

    {data?.abstract_xml ? (
      <FileButton href={data.abstract_xml} label="Abstract XML" />
    ) : null}

    {data?.opr_pdf ? (
      <FileButton href={data.opr_pdf} label="Peer review comments" />
    ) : null}
  </Box>
);

export default ArticleDownloadBox;
